class BonusManager {
    static bonusStepType = 1;
    static bonusType = 2;

    static depositBonusType = 1;
    static reloadBonusType = 2;
    static activityTrophyBonusType = 3;
    static freespinBonusType = 4;
    static cashBonusType = 5;
    static bigFreespinBonusType = 6;
    static levelUpBonusType = 7;
    static trophySpecialBonusType = 8;
    static coinsBonusType = 9;
    static customerTagBonusType = 10;
    static cashPecentBonusType = 11;
    static freespinResultBonusType = 12;
    static jackpotWinBonusType = 13;

    static createdStatus = 1;
    static ongoingStatus = 2;
    static completedStatus = 3;
    static expiredStatus = 4;
    static forfeitureStatus = 5;
    static lostStatus = 6;
    static autocompletedStatus = 7;
    static issuedStatus = 8;

    constructor() {
        this._savedBonusInformation = {};
        this._savedActiveBonuses = {};
        this._types = [BonusManager.bonusStepType, BonusManager.bonusType];
    }

    _pushBonusStep(info) {
        if (!info.hasOwnProperty('id') || !info.hasOwnProperty('currencyBreakdown')) return false;
        if (typeof info.currencyBreakdown !== 'object') return false;

        this._savedBonusInformation[info.id] = {
            currencyBreakdown: info.currencyBreakdown,
            extraInfo: {},
        };
        for (const key in info) {
            if (key == 'currencyBreakdown' || key == 'id') continue;

            this._savedBonusInformation[info.id].extraInfo[key] = info[key];
        }
        return true;
    }

    _pushBonus(info) {
        if (!info.hasOwnProperty('id') || !info.hasOwnProperty('bonus_type')) return false;
        this._savedActiveBonuses[info.id] = {
            bonus_type: info.bonus_type,
        };
        return true;
    }

    /**
     * Returns true or false, depending on if the push was successful
     *
     * Register the bonus within the manager along the bonus step's currency breakdown. This breakdown is later used to retrieve all
     * information for the bonus step or the bonus. The given value should be an object that has id and currencyBreakdown as keys if bonusStep or id and bonus_type if a bonus.
     * The currency breakdown should be an object with the currency code as the key and the value - another object that has information key and value pairs.
     * Any other keys passed in the object will be stored as additional info and can be retrieved using the getExtraInfo function.
     *
     * If a bonus with the same id as a previously pushed bonus is passed, then the information for the old bonus will be overwritten
     *
     * value example for bonus step:
     *
     * {
     *     type: 1
     *     element: {
     *         id: '1',
     *         currencyBreakdown: {
     *              EUR: {
     *                  min_deposit: 10,
     *                  max_deposit: 20.5,
     *                  amount: 12.5,
     *              },
     *              ...
     *          },
     *          name: 'Example Bonus', // extra info about the bonus step
     *          ...
     *     }
     * }
     *
     * value example for bonus:
     * {
     *     type: 2,
     *     element: {
     *         id: '1',
     *         bonus_type: 1,
     *     },
     * }
     */
    push(value) {
        if (typeof value !== 'object' || value === null) return false;
        if (!value.hasOwnProperty('type') || !this._types.includes(value.type)) return false;
        if (!value.hasOwnProperty('element') || typeof value.element !== 'object') return false;
        switch(value.type) {
            case BonusManager.bonusStepType:
                return this._pushBonusStep(value.element);
            case BonusManager.bonusType:
                return this._pushBonus(value.element);
        }
        return false;
    }

    /**
     * Retrieves the stored currencyBreakdown for the given bonus step id
     * If none is found the null is returned
     * @param id
     * @param currency
     */
    getCurrencyBreakdown(id, currency) {
        if (!this._savedBonusInformation.hasOwnProperty(id) || !this._savedBonusInformation[id].currencyBreakdown.hasOwnProperty(currency))
            return null;
        return this._savedBonusInformation[id].currencyBreakdown[currency];
    }

    /**
     * Returns whether the bonus step is already stored in the manager
     * @param id
     * @returns {boolean}
     */
    hasBonusStep(id) {
        return this._savedBonusInformation.hasOwnProperty(id);
    }

    /**
     * Returns whether the bonus step has information about the currency
     * @param id
     * @param currency
     * @returns {boolean}
     */
    hasCurrencyBreakdown(id, currency) {
        return this._savedBonusInformation.hasOwnProperty(id) && this._savedBonusInformation[id].currencyBreakdown.hasOwnProperty(currency);
    }

    /**
     * Retrieves the stored extraInformation for the given bonus step
     *
     * If the bonus isn't found then the result is null, if the bonus had no extra information the function will return an empty object
     * @param id
     */
    getExtraInformation(id) {
        if (!this._savedBonusInformation.hasOwnProperty(id)) return null;
        return this._savedBonusInformation[id].extraInfo;
    }

    /**
     * Returns whether or not the user has an active bonus of the given type.
     *
     * @param bonusType
     * @returns {boolean}
     */
    hasActiveBonus(bonusType) {
        for (const key of Object.keys(this._savedActiveBonuses)) {
            if (this._savedActiveBonuses[key].bonus_type == bonusType) return true;
        }
        return false;
    }

    /**
     * Deletes the given bonus id from the list of active bonuses. Returns true if the key existed and false if it didn't
     *
     * @param id
     * @returns {boolean}
     */
    removeActiveBonus(id) {
        if (this._savedActiveBonuses.hasOwnProperty(id)) {
            delete this._savedActiveBonuses[id];
            return true;
        }
        return false;
    }

    hasActiveCashBonus() {
        return this.hasActiveBonus(BonusManager.depositBonusType)
            || this.hasActiveBonus(BonusManager.cashBonusType)
            || this.hasActiveBonus(BonusManager.cashPecentBonusType)
            || this.hasActiveBonus(BonusManager.freespinResultBonusType)
            || this.hasActiveBonus(BonusManager.jackpotWinBonusType);
    }

    hasActiveFreespinBonus() {
        return this.hasActiveBonus(BonusManager.freespinBonusType)
            || this.hasActiveBonus(BonusManager.bigFreespinBonusType);
    }
}

(() => {
    const savedBonuses = window.bonusManager || [];
    window.bonusManager = new BonusManager();
    for (const bonus of savedBonuses) {
        bonusManager.push(bonus);
    }
})();